import { useRef, useEffect, useState } from "react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";

interface Data {
  icon: string;
  title: string;
}
type Props = {
  data: Data;
  setData: (data: Data) => void;
  titlePlaceholder?: string;
  disableEdit?: boolean;
};

const CreativeReportTitle = (props: Props) => {
  const { data, setData, titlePlaceholder, disableEdit } = props;
  const [title, setTitle] = useState(data.title);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  const previousTitle = useRef(data.title);

  // Update local title when prop changes
  useEffect(() => {
    if (data.title !== title) {
      setTitle(data.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.title]);

  // Only update parent when title actually changes and on blur
  const handleTitleBlur = () => {
    if (title !== previousTitle.current) {
      setData({ ...data, title });
      previousTitle.current = title;
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current?.contains(e.target as Node) &&
        !emojiInputRef.current?.contains(e.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setData({ ...data, icon: emojiData.emoji });
    setShowEmojiPicker(false);
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="w-full flex items-center gap-2">
        <div className="relative">
          <input
            ref={emojiInputRef}
            className={`w-8 h-8 transition-colors flex items-center justify-center text-center text-[21.3px] cursor-pointer outline-none focus:outline-none border-none focus:border-none caret-transparent squircle squircle-radius-[8px] squircle-smooth-[1] squircle-fill-[#F6F6F6] ${!disableEdit ? "hover:squircle-fill-[#EAEAE6]" : "hover:squircle-fill-[#F6F6F5]"}`}
            value={data.icon}
            onChange={() => {}}
            maxLength={1}
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={disableEdit}
          />
          {showEmojiPicker && (
            <div ref={emojiPickerRef} className="absolute z-20 top-[100%] mt-1">
              <EmojiPicker onEmojiClick={onEmojiClick} />
            </div>
          )}
        </div>
        <input
          className="flex-1 overflow-hidden text-brand-primary-new/80 text-ellipsis whitespace-nowrap text-[16px] font-medium leading-[30.651px] tracking-[0.44px] border-none outline-none placeholder:overflow-hidden placeholder:text-[#67749B] placeholder:text-ellipsis placeholder:text-[16px] placeholder:font-medium placeholder:tracking-[0.44px] bg-background-gray translate-y-[1px]"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleTitleBlur}
          placeholder={titlePlaceholder}
          disabled={disableEdit}
        />
      </div>
    </div>
  );
};

export default CreativeReportTitle;
